import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, Row, Select, Space, Statistic, Table, Typography } from "antd";
import { useMemo, useState } from "react";

import { useOverview } from "../../api/candidates";
import { formatTime } from "../../api/format";
import { useNames } from "../../api/names";
import type { Group, SiteStats } from "../../api/types";

const STAT_COLUMNS = [
  { title: "考生数", dataIndex: "total", align: "right" as const },
  { title: "已登录", dataIndex: "loggedIn", align: "right" as const },
  { title: "已提交", dataIndex: "submitted", align: "right" as const },
  { title: "已关闭", align: "right" as const, render: (_: unknown, row: { total: number; enabled: number }) => row.total - row.enabled }
];

interface RegionStats {
  regionId: number;
  sites: Set<number>;
  total: number;
  enabled: number;
  loggedIn: number;
  submitted: number;
}

export function OverviewPage() {
  const overview = useOverview();
  const { catalog, groupName, regionName, siteName, roomName } = useNames();
  const [group, setGroup] = useState<Group>();
  const rows = useMemo(
    () => (overview.data?.sites ?? []).filter((site) => group === undefined || site.group === group),
    [overview.data, group]
  );
  const roomRows = useMemo(
    () => (overview.data?.rooms ?? []).filter((room) => group === undefined || room.group === group),
    [overview.data, group]
  );
  const regionRows = useMemo(() => {
    const byRegion = new Map<number, RegionStats>();
    for (const site of rows) {
      const region = byRegion.get(site.regionId) ?? { regionId: site.regionId, sites: new Set<number>(), total: 0, enabled: 0, loggedIn: 0, submitted: 0 };
      region.sites.add(site.siteId);
      region.total += site.total;
      region.enabled += site.enabled;
      region.loggedIn += site.loggedIn;
      region.submitted += site.submitted;
      byRegion.set(site.regionId, region);
    }
    return [...byRegion.values()].sort((a, b) => a.regionId - b.regionId);
  }, [rows]);
  const totals = rows.reduce(
    (sum, site) => ({
      total: sum.total + site.total,
      enabled: sum.enabled + site.enabled,
      loggedIn: sum.loggedIn + site.loggedIn,
      submitted: sum.submitted + site.submitted
    }),
    { total: 0, enabled: 0, loggedIn: 0, submitted: 0 }
  );
  const groups = catalog.data?.groups ?? [];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Space wrap>
        {groups.length > 1 && (
          <Select<Group>
            allowClear
            placeholder="全部组别"
            style={{ width: 160 }}
            value={group}
            onChange={setGroup}
            options={groups.map((item) => ({ value: item.id, label: item.name }))}
          />
        )}
        <Button icon={<ReloadOutlined />} loading={overview.isFetching} onClick={() => void overview.refetch()}>
          刷新
        </Button>
        <Typography.Text type="secondary">
          统计时间 {formatTime(overview.data?.generatedAt)}，每 30 秒自动刷新
        </Typography.Text>
      </Space>
      {overview.error && <Alert type="error" showIcon message={overview.error.message} />}
      <Row gutter={[16, 16]}>
        {[
          ["考生总数", totals.total],
          ["已登录", totals.loggedIn],
          ["已提交（实际参加）", totals.submitted],
          ["已关闭账号", totals.total - totals.enabled]
        ].map(([title, value]) => (
          <Col key={title} xs={12} md={6}>
            <Card><Statistic title={title} value={value} /></Card>
          </Col>
        ))}
      </Row>
      <Table<RegionStats>
        rowKey="regionId"
        loading={overview.isLoading}
        dataSource={regionRows}
        pagination={false}
        size="middle"
        expandable={{
          expandedRowRender: (region) => (
            <Table<SiteStats>
              rowKey={(row) => `${row.siteId}:${row.group}`}
              size="small"
              pagination={false}
              dataSource={rows.filter((site) => site.regionId === region.regionId)}
              expandable={{
                rowExpandable: (row) => roomRows.some((room) => room.siteId === row.siteId && room.group === row.group),
                expandedRowRender: (row) => (
                  <Table<SiteStats>
                    rowKey={(room) => `${room.siteId}:${room.roomId}:${room.group}`}
                    size="small"
                    pagination={false}
                    dataSource={roomRows.filter((room) => room.siteId === row.siteId && room.group === row.group)}
                    columns={[{ title: "考场", dataIndex: "roomId", render: (id: number) => roomName(id) }, ...STAT_COLUMNS]}
                  />
                )
              }}
              columns={[
                { title: "考点", dataIndex: "siteId", render: (id: number) => `${siteName(id)}（${id}）` },
                { title: "组别", dataIndex: "group", render: groupName },
                ...STAT_COLUMNS
              ]}
            />
          )
        }}
        columns={[
          { title: "考点区域", dataIndex: "regionId", render: regionName, sorter: (a, b) => a.regionId - b.regionId },
          { title: "考点数", align: "right", render: (_, region) => region.sites.size },
          ...STAT_COLUMNS
        ]}
      />
    </Space>
  );
}
